// API utility functions and constants

// Use environment variable or fallback to localhost
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Helper function for making API requests
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<any>} - The response data
 */
export const fetchFromApi = async (endpoint, options = {}) => {
  try {
    // Handle complete URLs vs relative endpoints
    // If endpoint is a full URL (starts with http:// or https://), use it as is
    // Otherwise, append it to API_BASE_URL
    let url;
    if (!endpoint) {
      //console.error('No endpoint provided to fetchFromApi');
      throw new Error('No endpoint provided to fetchFromApi');
    }
    
    if (typeof endpoint !== 'string') {
      //console.error('Invalid endpoint provided to fetchFromApi:', endpoint);
      throw new Error(`Invalid endpoint provided: ${JSON.stringify(endpoint)}`);
    }
    
    if (endpoint.startsWith('http')) {
      url = endpoint;
    } else {
      url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    }
    
    console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`);
    console.log(`🌐 API Request Source: ${options.caller || 'Unknown'}`);
    console.log(`🌐 API_BASE_URL: ${API_BASE_URL}`);
    
    // Ensure default headers
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
      ...options.headers
    };
    
    // console.log('Request headers:', headers);
    if (options.body) {
      // console.log('Request body:', typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    }
    
    // Start timing the request
    const startTime = Date.now();
    
    const response = await fetch(url, {
      ...options,
      headers,
      // Add credentials to include cookies if needed
      credentials: 'include'
    });
    
    const responseTime = Date.now() - startTime;
    console.log(`⏱️ API Response time: ${responseTime}ms, Status: ${response.status}`);
    
    // Check if the response is OK (status in the range 200-299)
    if (!response.ok) {
      // Try to parse error message from response
      let errorData;
      let responseText;
      
      try {
        responseText = await response.text();
        console.error('Error response text:', responseText);
        
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { message: `API request failed with status ${response.status}` };
        }
      } catch {
        errorData = { message: `API request failed with status ${response.status}` };
      }
      
      console.error('API error details:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers]),
        errorData
      });
      
      // Create an error object with status information
      const apiError = new Error(errorData.message || errorData.title || `API request failed with status ${response.status}`);
      apiError.status = response.status;
      apiError.statusText = response.statusText;
      apiError.response = response;
      apiError.data = errorData; // Add the parsed error data for quota detection
      
      throw apiError;
    }
    
    // Log response headers for debugging
    // console.log('Response headers:', Object.fromEntries([...response.headers]));
    
    // Check if there's content to parse
    const contentType = response.headers.get('content-type');
    // console.log('Response content-type:', contentType);
    
    if (contentType && contentType.includes('application/json')) {
      const jsonData = await response.json();
      console.log('API response data:', jsonData);
      return jsonData;
    } else {
      // If not JSON, log the raw text
      const textData = await response.text();
      console.log('API response text:', textData);
      
      // Try to parse as JSON anyway in case content-type header is wrong
      try {
        if (textData && textData.trim()) {
          return JSON.parse(textData);
        }
      } catch (e) {
        // console.log('Not JSON content, returning as plain text');
      }
      
      // Return the text data for non-JSON responses (like plain text notes)
      return textData || '';
    }
  } catch (error) {
    // console.error('API request error:', error);
    throw error;
  }
};

/**
 * Handles pagination for API endpoints that support it
 * @param {string} endpoint - The base API endpoint
 * @param {Object} params - Query parameters including pagination params
 * @returns {Promise<any>} - The paginated response data
 */
export const fetchPaginatedData = async (endpoint, params = {}) => {
  // Convert params object to query string
  const queryParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
    
  const url = queryParams ? `${endpoint}?${queryParams}` : endpoint;
  return fetchFromApi(url);
};
